import * as React from 'react';
import type { ReactNode } from 'react';
import type { WrappedComponentProps, IntlShape } from 'react-intl-next';
import { injectIntl, useIntl } from 'react-intl-next';
import type {
    hideFeedbackCollector as hideFeedbackCollectorType,
    updateAdditionalFieldsInFeedbackCollector as updateAdditionalFieldsInFeedbackCollectorType,
} from 'state/actions/feedback-collector';
import type { showSuccessFlag as showSuccessFlagType } from 'state/actions/flags';
import type { FeedbackCollectorUIState } from 'state/ui/feedback-collector';
import FeedbackCollector from '@atlaskit/feedback-collector';
import type SelectValue from '@atlaskit/feedback-collector/dist/types/types';
import { EnvType, getEnv } from '@atlassian/help-center-common-util/env';
import { withAdditionalFieldsComponent } from '../with-additional-fields-component';
import { linkToSupport, stagingFeedbackWidgetData, productionFeedbackWidgetData } from './constants';
import messages from './messages';
import type { FeedbackWidgetData } from './types';

interface Props {
    email: string;
    name: string;
    env: EnvType;
    isOpen: FeedbackCollectorUIState['shouldOpenFeedbackWidget'];
    showTypeField: FeedbackCollectorUIState['showTypeField'];
    additionalFields: FeedbackCollectorUIState['additionalFields'];
    CustomComponent?: FeedbackCollectorUIState['CustomComponent'];
    hideFeedbackCollector: typeof hideFeedbackCollectorType;
    updateAdditionalFieldsInFeedbackCollector: typeof updateAdditionalFieldsInFeedbackCollectorType;
    showSuccessFlag: typeof showSuccessFlagType;
}

const getFeedbackGroupLabels = (intl: IntlShape): Record<SelectValue.SelectValue, SelectValue.SelectOptionDetails> => {
    return {
        question: {
            fieldLabel: intl.formatMessage(messages.questionFieldLabel),
            selectOptionLabel: intl.formatMessage(messages.questionSelectOptionLabel),
        },
        comment: {
            fieldLabel: intl.formatMessage(messages.commentFieldLabel),
            selectOptionLabel: intl.formatMessage(messages.commentSelectOptionLabel),
        },
        bug: {
            fieldLabel: intl.formatMessage(messages.bugFieldLabel),
            selectOptionLabel: intl.formatMessage(messages.bugSelectOptionLabel),
        },
        suggestion: {
            fieldLabel: intl.formatMessage(messages.suggestionFieldLabel),
            selectOptionLabel: intl.formatMessage(messages.suggestionSelectOptionLabel),
        },
        empty: {
            fieldLabel: intl.formatMessage(messages.emptyFieldLabel),
            selectOptionLabel: intl.formatMessage(messages.emptySelectOptionLabel),
        },
    };
};

const FeedbackPreamble = (props: { CustomComponent?: React.FC }) => {
    const { formatMessage } = useIntl();
    const CustomComponent = props.CustomComponent;

    return (
        <>
            {formatMessage(messages.feedbackPreambleTitleText, {
                p: (chunks: ReactNode) => <p>{chunks}</p>,
                br: <br />,
            })}
            {CustomComponent && <CustomComponent />}
        </>
    );
};

const CanBeContactedLabel = () => {
    const { formatMessage } = useIntl();
    return (
        <p>
            {formatMessage(messages.canContactLabelText, {
                a: (chunks: ReactNode) => (
                    <a href={linkToSupport} target="_blank" rel="noopener noreferrer">
                        {chunks}
                    </a>
                ),
            })}
        </p>
    );
};

const EnrolLabel = () => {
    const { formatMessage } = useIntl();
    return (
        <p>
            {formatMessage(messages.enrolLabelText, {
                a: (chunks: ReactNode) => (
                    <a href={linkToSupport} target="_blank" rel="noopener noreferrer">
                        {chunks}
                    </a>
                ),
            })}
        </p>
    );
};

export const DisplayFeedback: React.FC<Props & WrappedComponentProps> = ({
    email,
    name,
    isOpen,
    showTypeField,
    additionalFields,
    CustomComponent,
    intl,
    hideFeedbackCollector,
    showSuccessFlag,
    updateAdditionalFieldsInFeedbackCollector,
    env = getEnv().environment,
}) => {
    const feedbackWidgetData: FeedbackWidgetData =
        env === EnvType.PROD ? productionFeedbackWidgetData : stagingFeedbackWidgetData;

    const close = React.useCallback(() => {
        hideFeedbackCollector();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const displayFlag = React.useCallback(() => {
        showSuccessFlag(messages.feedbackSentFlagTitle, undefined, messages.feedbackSentFlagDescription);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onAdditionalFieldChange = React.useCallback(
        (fieldId: string, fieldValue: string | object) => {
            const fields = { ...additionalFields };
            fields[fieldId] = fieldValue;
            updateAdditionalFieldsInFeedbackCollector(fields);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [additionalFields]
    );

    const additionaFieldsForFeedbackCollector = React.useMemo(() => {
        if (additionalFields) return Object.entries(additionalFields).map(([id, value]) => ({ id, value }));
        return [];
    }, [additionalFields]);

    return (
        <div data-testid="portal-feedback-collector">
            {isOpen && (
                <FeedbackCollector
                    onClose={close}
                    onSubmit={displayFlag}
                    email={email}
                    name={name}
                    requestTypeId={feedbackWidgetData.requestTypeId}
                    embeddableKey={feedbackWidgetData.embeddableKey}
                    feedbackTitle={intl.formatMessage(messages.feedbackTitle)}
                    showTypeField={showTypeField}
                    feedbackTitleDetails={
                        <FeedbackPreamble
                            CustomComponent={
                                CustomComponent
                                    ? withAdditionalFieldsComponent({
                                          additionalFields,
                                          onChange: onAdditionalFieldChange,
                                      })(CustomComponent)
                                    : undefined
                            }
                        />
                    }
                    canBeContactedLabel={<CanBeContactedLabel />}
                    enrolInResearchLabel={<EnrolLabel />}
                    summaryPlaceholder={intl.formatMessage(messages.summaryPlaceholder)}
                    customerNameFieldId={feedbackWidgetData.customerNameFieldId}
                    canBeContactedFieldId={feedbackWidgetData.canBeContactedField.fieldId}
                    canBeContactedDefaultValue={feedbackWidgetData.canBeContactedField.defaultValue}
                    enrollInResearchFieldId={feedbackWidgetData.enrollInResearchField.fieldId}
                    enrollInResearchDefaultValue={feedbackWidgetData.enrollInResearchField.defaultValue}
                    typeFieldId={feedbackWidgetData.typeField.fieldId}
                    typeBugDefaultValue={feedbackWidgetData.typeField.bugDefaultValue}
                    typeCommentDefaultValue={feedbackWidgetData.typeField.commentDefaultValue}
                    typeSuggestionDefaultValue={feedbackWidgetData.typeField.suggestionDefaultValue}
                    typeQuestionDefaultValue={feedbackWidgetData.typeField.questionDefaultValue}
                    typeEmptyDefaultValue={feedbackWidgetData.typeField.emptyDefaultValue}
                    submitButtonLabel={intl.formatMessage(messages.submitButtonLabel)}
                    cancelButtonLabel={intl.formatMessage(messages.cancelButtonLabel)}
                    feedbackGroupLabels={getFeedbackGroupLabels(intl)}
                    additionalFields={additionaFieldsForFeedbackCollector}
                />
            )}
        </div>
    );
};

export default injectIntl(DisplayFeedback);
